import clsx from 'clsx';

import { SessionListType } from '@/types';
import { Typography } from '@/ui';

import { SessionCard } from '../Card';
import SessionCardSkeleton from '../Card/Skeleton';
import MemberSessionCard from '../Member/MemberSessionCard';

import type { Session } from '@/types';
import type { FC } from 'react';

interface Props {
  enforceMemberRescheduleMinimumNotice?: (session: Session) => boolean;
  hideIfEmpty?: boolean;
  loading?: boolean;
  // Render the redesigned, date-led member row (`MemberSessionCard`) instead of
  // the legacy title-led `SessionCard`. Callers opt in only for a member
  // viewing their own list:
  // `MemberSessionCard` links to `/sessions/:id`, which is wrong for the
  // coach-viewing-a-member mount of this page.
  memberRows?: boolean;
  onCancel?: (session: Session) => void;
  onFeedback?: (session: Session) => void;
  onReschedule?: (session: Session) => void;
  presentation?: 'default' | 'matched';
  sessions: Record<string, Session[]>;
  type: SessionListType;
}

const isNativeOnlyAppointmentRow = (session: Session): boolean =>
  Boolean(
    session.bookingSource === 'moments' &&
    session.sourceAppointmentId &&
    session.id === session.sourceAppointmentId,
  );

const SessionList: FC<Props> = ({
  enforceMemberRescheduleMinimumNotice = undefined,
  hideIfEmpty = false,
  loading = false,
  memberRows = false,
  onCancel = undefined,
  onFeedback = undefined,
  onReschedule = undefined,
  presentation = 'default',
  sessions,
  type,
}) => {
  const useMemberRows = memberRows;
  const usePastMemberRows = useMemberRows && type === SessionListType.Past;

  if (loading) {
    return (
      <>
        <Typography
          className="mt-8 select-none px-2 text-cement-200 md:px-0"
          variant="subtitle1"
        >
          {SessionListType[type]} sessions
        </Typography>
        <div className="pt-4">
          <SessionCardSkeleton />
          <SessionCardSkeleton />
          <SessionCardSkeleton />
          <SessionCardSkeleton />
        </div>
      </>
    );
  }

  if (!Object.keys(sessions).length) {
    if (hideIfEmpty) return null;

    return (
      <div className="mt-20 flex flex-1 flex-col items-center justify-center">
        <img
          alt=""
          className=""
          height="147"
          src="https://mento-space.nyc3.digitaloceanspaces.com/assets/sessions-meet.svg"
          width="127"
        />
        <Typography className="headline-sm mt-4 text-cement-400" variant="h6">
          No {SessionListType[type]} sessions
        </Typography>
        <Typography className="mt-4 text-cement-400">
          Sessions scheduled via Mento will show up here
        </Typography>
      </div>
    );
  }

  return (
    <>
      {Object.entries(sessions).map(([name = '', values = []]) => {
        const [firstSession] = values;
        if (!firstSession) return null;
        const railLabel =
          presentation === 'matched'
            ? new Intl.DateTimeFormat('en-US', {
                month: 'long',
                year: 'numeric',
              }).format(new Date(firstSession.startedAt))
            : name;

        return (
          <div
            key={name}
            className={clsx('pt-8', {
              'grid items-start gap-4 md:grid-cols-[96px_minmax(0,1fr)]':
                presentation === 'matched' && !usePastMemberRows,
            })}
          >
            {/* Figma "Past Sessions" (17420:41868) types the month header as
                typography/subtitle2 (Bold 14) in text/primary, 20px above the
                first card. The legacy list keeps its subtitle1 grey header. */}
            <Typography
              className={clsx(
                'px-2 md:px-0',
                usePastMemberRows ? 'pb-5 text-cement-500' : 'text-cement-400',
                presentation !== 'matched' && !usePastMemberRows && 'pb-4',
              )}
              variant={usePastMemberRows ? 'subtitle2' : 'subtitle1'}
            >
              {railLabel}
            </Typography>
            {/* The Figma "Past Sessions" frame spaces member rows 20px
                apart; the legacy list keeps its tighter 12px. */}
            <div
              className={clsx(
                'flex flex-col',
                usePastMemberRows ? 'gap-5' : 'gap-3',
              )}
            >
              {values.map((session) =>
                useMemberRows ? (
                  <MemberSessionCard
                    key={session.id}
                    pastListLayout={type === SessionListType.Past}
                    pastReadOnly={
                      type === SessionListType.Past &&
                      isNativeOnlyAppointmentRow(session)
                    }
                    session={session}
                    type={type}
                    onCancel={onCancel}
                    onFeedback={onFeedback}
                    onReschedule={onReschedule}
                  />
                ) : (
                  <SessionCard
                    key={session.id}
                    enforceMemberRescheduleMinimumNotice={enforceMemberRescheduleMinimumNotice?.(
                      session,
                    )}
                    presentation={presentation}
                    session={session}
                    type={type}
                    onCancel={onCancel}
                    onFeedback={onFeedback}
                    onReschedule={onReschedule}
                  />
                ),
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default SessionList;
